import {Overlay} from "@angular/cdk/overlay"
import {Component, inject, OnDestroy, ChangeDetectionStrategy} from "@angular/core"
import {FormsModule} from "@angular/forms"
import {MatButtonModule} from "@angular/material/button"
import {MAT_DATE_FORMATS} from "@angular/material/core"
import {MatDatepickerModule} from "@angular/material/datepicker"
import {MatMenu, MatMenuItem, MatMenuModule} from "@angular/material/menu"
import {MatTooltipModule} from "@angular/material/tooltip"
import {RouterLink, RouterOutlet} from "@angular/router"
import {PictureDetailsFragment} from "@app/platform/components/pictures/picture-details/picture-details.generated"
import {RoutedDialogComponent} from "@common/components/dialogs/routed-dialog/routed-dialog.component"
import {InputContainerComponent} from "@common/components/inputs/input-container/input-container.component"
import {NativeInputTextAreaComponent} from "@common/components/inputs/native/native-input-text-area/native-input-text-area.component"
import {TagSelectComponent} from "@common/components/inputs/select/tag-select/tag-select.component"
import {SectionComponent} from "@common/components/item"
import {fetchThrowingErrors} from "@common/helpers/api/fetch"
import {mutateReturningDataAndErrors, mutateThrowingErrors} from "@common/helpers/api/mutate"
import {DialogSize} from "@common/models/dialogs"
import {CM_SHORT_DATE_FORMAT} from "@common/models/settings/settings"
import {OrganizationsService} from "@common/services/organizations/organizations.service"
import {Enums} from "@enums"
import {ContentTypeModel, MutationUpdatePictureInput, NextActor, PictureState} from "@generated"
import {BaseDetailsComponent} from "@platform/components/base/base-details/base-details.component"
import {ActivitySectionComponent} from "@platform/components/details/activity-section/activity-section.component"
import {FilesSectionComponent} from "@platform/components/details/files-section/files-section.component"
import {PictureAssetsSectionComponent} from "@platform/components/details/picture-assets-section/picture-assets-section.component"
import {ProjectManagementTasksSectionComponent} from "@platform/components/details/project-management-tasks-section/project-management-tasks-section.component"
import {TitleSectionComponent} from "@platform/components/details/title-section/title-section.component"
import {AssociatedJobsTableComponent} from "@platform/components/jobs/associated-jobs-table/associated-jobs-table.component"
import {DetailsDialogLayoutComponent} from "@platform/components/layouts/details-dialog-layout/details-dialog-layout.component"
import {
    DeletePictureDetailsGQL,
    PictureDetailsGQL,
    PictureDetailsUpdatePictureGQL,
    UpdatePictureDetailsGQL,
} from "@platform/components/pictures/picture-details/picture-details.generated"
import {PictureRevisionSelectorComponent} from "@platform/components/pictures/picture-revision-selector/picture-revision-selector.component"
import {PictureStatisticsComponent} from "@platform/components/pictures/picture-statistics/picture-statistics.component"
import {TasksService} from "@platform/services/tasks/tasks.service"

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        DetailsDialogLayoutComponent,
        FormsModule,
        MatButtonModule,
        MatDatepickerModule,
        MatTooltipModule,
        PictureRevisionSelectorComponent,
        RouterLink,
        SectionComponent,
        AssociatedJobsTableComponent,
        InputContainerComponent,
        NativeInputTextAreaComponent,
        PictureStatisticsComponent,
        RoutedDialogComponent,
        MatMenu,
        MatMenuItem,
        MatMenuModule,
        FilesSectionComponent,
        ActivitySectionComponent,
        ProjectManagementTasksSectionComponent,
        TitleSectionComponent,
        PictureAssetsSectionComponent,
        RouterOutlet,
        TagSelectComponent,
    ],
    providers: [{provide: MAT_DATE_FORMATS, useValue: CM_SHORT_DATE_FORMAT}],
    selector: "cm-picture",
    styleUrls: ["picture-details.component.scss"],
    templateUrl: "picture-details.component.html",
})
export class PictureDetailsComponent extends BaseDetailsComponent<PictureDetailsFragment, Omit<MutationUpdatePictureInput, "id">> implements OnDestroy {
    override _contentTypeModel = ContentTypeModel.Picture
    override _fetchItem = fetchThrowingErrors(inject(PictureDetailsGQL))
    override _updateItem = mutateThrowingErrors(inject(UpdatePictureDetailsGQL))
    override _deleteItem = mutateReturningDataAndErrors(inject(DeletePictureDetailsGQL))

    override displayName = "picture"

    readonly organizations = inject(OrganizationsService)
    readonly overlay = inject(Overlay)
    readonly task = inject(TasksService)

    readonly updatePictureGql = inject(PictureDetailsUpdatePictureGQL)

    async updatePicture(data: Omit<MutationUpdatePictureInput, "id">, stateChange = false) {
        const pictureId = this.$item()?.id
        if (pictureId) {
            await this.notifications.withUserFeedback(
                () =>
                    mutateThrowingErrors(this.updatePictureGql)({
                        input: {
                            id: pictureId,
                            ...data,
                        },
                    }),
                {
                    success: "Picture updated.",
                    error: "Failed to update picture.",
                },
            )
            if (stateChange) {
                this.refresh.item(this.$item())
            }
        }
    }

    async afterAssetChange() {
        this.refresh.item(this.$item())
    }

    canSubmitPicture(picture: PictureDetailsFragment) {
        return picture.state === PictureState.Draft && !this.silo.$silo()?.systemRole && this.silo.$silo()?.organization?.id === picture.organization.id
    }

    canMovePictureAlongPipeline(picture: PictureDetailsFragment) {
        return !this.silo.$silo()?.systemRole && picture && Enums.InPipelineStates.Picture.includes(picture.state)
    }

    ngOnDestroy(): void {
        this.refresh.item(this.$item())
    }

    protected readonly ContentTypeModel = ContentTypeModel
    protected readonly NextActor = NextActor
    protected readonly PictureState = PictureState
    protected readonly DialogSize = DialogSize
}
